import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { mensagensAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mail, MailOpen, Trash2, Loader2 } from 'lucide-react';

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export const Mensagens = () => {
  const [novas, setNovas] = useState([]);
  const [lidas, setLidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('novas');
  usePageTitle('Mensagens');

  useEffect(() => { fetchMensagens(); }, []);

  const fetchMensagens = async () => {
    setLoading(true);
    try {
      const [novasRes, lidasRes] = await Promise.all([
        mensagensAPI.getByStatus(false),
        mensagensAPI.getByStatus(true)
      ]);
      setNovas(novasRes.data);
      setLidas(lidasRes.data);
    } catch (error) {
      console.error('Error fetching mensagens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarLida = async (id) => {
    try {
      await mensagensAPI.marcarLida(id);
      fetchMensagens();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que deseja remover esta mensagem?')) return;
    try {
      await mensagensAPI.delete(id);
      fetchMensagens();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const MessageCard = ({ msg, isNew }) => (
    <Card className={`border ${isNew ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`} data-testid={`mensagem-${msg.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500 mb-2">{formatDateTime(msg.created_at)}</div>
            <div
              className="prose prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: msg.conteudo }}
            />
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isNew && (
              <Button variant="ghost" size="sm" onClick={() => handleMarcarLida(msg.id)} title="Marcar como lida" data-testid={`mark-read-${msg.id}`}>
                <MailOpen className="h-4 w-4 text-amber-600" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => handleDelete(msg.id)} title="Remover" data-testid={`delete-msg-${msg.id}`}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const EmptyState = ({ text }) => (
    <div className="text-center py-12 text-slate-500">
      <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
      <p>{text}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="mensagens-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Mensagens</h1>
        <p className="text-slate-500 mt-1">Mensagens da receção</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="mensagens-tabs">
          <TabsTrigger value="novas" data-testid="tab-novas">
            Novas {novas.length > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5">{novas.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="lidas" data-testid="tab-lidas">Lidas</TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <>
            <TabsContent value="novas" className="space-y-3 mt-4">
              {novas.length === 0 ? <EmptyState text="Sem mensagens novas" /> : novas.map(msg => <MessageCard key={msg.id} msg={msg} isNew />)}
            </TabsContent>
            <TabsContent value="lidas" className="space-y-3 mt-4">
              {lidas.length === 0 ? <EmptyState text="Sem mensagens lidas" /> : lidas.map(msg => <MessageCard key={msg.id} msg={msg} isNew={false} />)}
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
};
